import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { forkJoin, of } from 'rxjs';
import { Location } from '../../location/location.model';
import { LazyLocationPickerDirective } from '../../shared/location-picker/lazy-location-picker.directive';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageDetailHeroComponent } from '../../shared/page-layout/page-detail-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { Customer } from '../../customer/customer.model';
import { CustomerService } from '../../customer/customer.service';
import { LogisticsProjectsService } from '../../logistics-projects/logistics-projects.service';
import { ShipmentService } from '../../shipment/shipment.service';
import { Shipment } from '../../shipment/models/shipment';
import { ShipmentDelivery } from '../../shipment/models/delivery/shipment-delivery.model';
import { WarehousingItem, WarehousingMasterData } from '../../warehousing/warehousing.model';
import { WarehousingService } from '../../warehousing/warehousing.service';
import { FieldResourcesPanelComponent } from '../../field-work/field-resources-panel.component';
import {
  CrossDockOrchestration,
  PublicOrderReleaseTracking,
  PublicTrackingDocument,
  PublicTrackingEvent,
  PublicTrackingLeg,
  TransportOrderRelease,
  TransportOrderReleaseLine
} from '../transport-planning.model';
import { TransportPlanningService } from '../transport-planning.service';
import { ReleaseCancellationImpact } from '../transport-planning.model';
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';

@Component({
  selector: 'app-transport-release-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    DialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    SelectModule,
    TableModule,
    TabsModule,
    TagModule,
    ToastModule,
    TextareaModule,
    TooltipModule,
    PageShellComponent,
    PageDetailHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent,
    FieldResourcesPanelComponent,
    LazyLocationPickerDirective
  ],
  providers: [MessageService],
  templateUrl: './transport-release-view.component.html',
  styleUrls: ['./transport-release-view.component.css']
})
export class TransportReleaseViewComponent implements OnInit {
  private readonly tabKeys = ['overview', 'lines', 'planning', 'tracking', 'orchestration'] as const;
  release?: TransportOrderRelease;
  tracking?: PublicOrderReleaseTracking;
  orchestration?: CrossDockOrchestration;
  releaseCustomer?: Customer;
  plannedShipment?: Shipment;
  logisticsProjectNumber?: string;
  isLoading = false;
  message = '';
  cancelDialogVisible = false;
  cancelDialogError = '';
  cancellationReason = '';
  cancellationImpact?: ReleaseCancellationImpact;
  isCancelling = false;
  activeTabIndex = 0;
  locationDialogVisible = false;
  locationDialogEndpoint: 'origin' | 'destination' = 'origin';
  selectedLocation?: Location | null;
  isAssigningLocation = false;
  inventoryItemsById = new Map<number, WarehousingItem>();
  warehousesById = new Map<number, WarehousingMasterData>();
  zonesById = new Map<number, WarehousingMasterData>();
  conditionsById = new Map<number, WarehousingMasterData>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: TransportPlanningService,
    private shipmentService: ShipmentService,
    private logisticsProjectsService: LogisticsProjectsService,
    private warehousingService: WarehousingService,
    private customerService: CustomerService,
    private messageService: MessageService,
    private permissionService: PermissionService
  ) {}

  canCancelRelease(): boolean {
    return !!this.release?.releaseId && !this.release.parentReleaseId && this.release.releaseRole !== 'CHILD'
      && this.release.releaseStatus !== 'CANCELLED'
      && this.permissionService.hasPermission(app_permissions.transport_planning.plans.apply);
  }

  openCancelDialog(): void {
    if (!this.canCancelRelease() || !this.release?.releaseId) return;
    this.cancelDialogError = '';
    this.cancellationReason = '';
    this.cancellationImpact = undefined;
    this.cancelDialogVisible = true;
    this.service.getReleaseCancellationImpact(this.release.releaseId).subscribe({
      next: impact => this.cancellationImpact = impact,
      error: error => this.cancelDialogError = error?.error?.detail || error?.error?.message || 'Could not load cancellation impact.'
    });
  }

  cancelRelease(): void {
    if (!this.canCancelRelease() || !this.release?.releaseId || !this.cancellationImpact || this.isCancelling) return;
    const reason = this.cancellationReason.trim();
    if (!reason || reason.length > 500) {
      this.cancelDialogError = 'Enter a reason of 1 to 500 characters.';
      return;
    }
    this.isCancelling = true;
    this.service.cancelRelease(this.release.releaseId, reason).subscribe({
      next: () => {
        this.cancelDialogVisible = false;
        this.isCancelling = false;
        this.load(this.release!.releaseId!);
        this.messageService.add({ severity: 'success', summary: 'Release family cancelled' });
      },
      error: error => {
        this.cancelDialogError = error?.error?.detail || error?.error?.message || 'Could not cancel release.';
        this.isCancelling = false;
      }
    });
  }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      this.activeTabIndex = this.resolveTabIndex(params.get('tab'));
    });
    this.route.paramMap.subscribe(params => {
      const releaseId = Number(params.get('releaseId'));
      if (!Number.isFinite(releaseId) || releaseId <= 0) {
        this.message = 'Invalid order release.';
        return;
      }
      this.load(releaseId);
    });
  }

  load(releaseId: number): void {
    this.isLoading = true;
    this.message = '';
    this.tracking = undefined;
    this.orchestration = undefined;
    this.releaseCustomer = undefined;
    this.plannedShipment = undefined;
    this.logisticsProjectNumber = undefined;
    this.inventoryItemsById.clear();
    this.warehousesById.clear();
    this.zonesById.clear();
    this.conditionsById.clear();
    this.service.getRelease(releaseId).subscribe({
      next: release => {
        this.release = release;
        this.isLoading = false;
        this.loadTracking(release);
        this.loadOrchestration(release);
        this.loadReleaseCustomer(release);
        this.loadPlannedShipment(release);
        this.loadLogisticsProject(release);
        this.loadInventoryItems(release);
        this.loadLineMasterData(release);
      },
      error: () => {
        this.release = undefined;
        this.message = 'Could not load order release.';
        this.isLoading = false;
      }
    });
  }

  onTabChange(value: string | number): void {
    this.activeTabIndex = Number(value);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: this.tabKeys[this.activeTabIndex] || this.tabKeys[0] },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  heroSubtitle(): string {
    if (!this.release) {
      return '';
    }
    return [
      this.release.sourceType,
      this.roleLabel(),
      this.customerLabel(),
      this.release.sourceNumber || this.release.sourceId,
      this.routeSummary(),
      `Need ${this.formatDateTime(this.needDate())}`,
      this.loadSummary()
    ].filter(Boolean).join(' | ');
  }

  statusSeverity(status?: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    const value = (status || '').toUpperCase();
    if (value.includes('RESCHEDULED')) return 'warn';
    if (value.includes('WAITING_PREVIOUS_LEG')) return 'warn';
    if (value.includes('RELEASED') || value.includes('READY') || value.includes('APPROVED') || value.includes('COMPLETE')) return 'success';
    if (value.includes('ERROR') || value.includes('FAIL') || value.includes('REJECT') || value.includes('CANCEL')) return 'danger';
    if (value.includes('SPLIT')) return 'warn';
    if (value.includes('DRAFT') || value.includes('PENDING')) return 'warn';
    if (value.includes('VALIDATION') || value.includes('PLANNED')) return 'info';
    return 'secondary';
  }

  releaseHeroStatusClass(): string {
    const status = (this.release?.releaseStatus || 'DRAFT').toUpperCase();
    if (status.includes('RESCHEDULED')) return 'status-in-progress';
    if (status.includes('WAITING_PREVIOUS_LEG')) return 'status-in-progress';
    if (status.includes('RELEASED') || status.includes('READY') || status.includes('COMPLETE')) return 'status-complete';
    if (status.includes('ERROR') || status.includes('FAIL') || status.includes('REJECT') || status.includes('CANCEL')) return 'status-cancelled';
    if (status.includes('PLANNED') || status.includes('SHIPMENT')) return 'status-in-progress';
    return 'status-draft';
  }

  formatStatusLabel(status?: string): string {
    return (status || 'Draft').replaceAll('_', ' ');
  }

  detailMetrics(): Array<{ label: string; value: string; hint?: string; tone?: 'success' | 'warn' | 'info' | 'danger' }> {
    return [
      {
        label: 'Customer',
        value: this.customerLabel(),
        hint: this.customerHint(),
        tone: this.hasCustomerContext() ? 'success' : 'danger'
      },
      {
        label: 'Load Demand',
        value: `${this.formatAmount(this.totalQuantity())} ${this.sharedUom()}`,
        hint: `${this.lineCount()} item line${this.lineCount() === 1 ? '' : 's'}`,
        tone: 'info'
      },
      {
        label: 'Need By',
        value: this.formatDateTime(this.needDate()),
        hint: 'Delivery or pickup commitment'
      },
      {
        label: 'Weight',
        value: `${this.formatAmount(this.totalWeight())} kg`,
        hint: `${this.formatAmount(this.totalVolume())} cbm`
      },
      {
        label: 'Execution',
        value: this.release?.plannedShipmentId ? 'Shipment created' : 'Planning pool',
        hint: this.release?.plannedShipmentId ? this.primaryShipmentLabel() : (this.release?.validationMessage || 'Ready for planning'),
        tone: this.release?.plannedShipmentId ? 'success' : 'warn'
      }
    ];
  }

  getLines(): TransportOrderReleaseLine[] {
    return this.release?.lines ?? [];
  }

  lineCount(): number {
    return this.getLines().length;
  }

  loadSummary(): string {
    const lines = this.getLines();
    if (!lines.length) {
      return 'No load lines';
    }
    return `${this.formatAmount(this.totalQuantity())} ${this.sharedUom()} across ${lines.length} line${lines.length === 1 ? '' : 's'}`;
  }

  routeSummary(): string {
    if (!this.release) {
      return '';
    }
    return `${this.release.originName || 'Start missing'} to ${this.release.destinationName || 'Destination missing'}`;
  }

  mapUrl(latitude?: number, longitude?: number): string | null {
    if (latitude === undefined || longitude === undefined || latitude === null || longitude === null) {
      return null;
    }
    return `https://maps.google.com?q=${latitude},${longitude}`;
  }

  locationEditRoute(locationId?: number): unknown[] {
    return ['/location/edit', locationId || 0];
  }

  locationCreateRoute(): unknown[] {
    return ['/location/create'];
  }

  locationCreateQuery(endpoint: 'origin' | 'destination'): Record<string, string | number | undefined> {
    return {
      releaseId: this.release?.releaseId,
      endpoint,
      returnUrl: this.router.url
    };
  }

  openLocationAssignment(endpoint: 'origin' | 'destination'): void {
    this.locationDialogEndpoint = endpoint;
    const currentId = endpoint === 'origin' ? this.release?.originLocationId : this.release?.destinationLocationId;
    const currentName = endpoint === 'origin' ? this.release?.originName : this.release?.destinationName;
    const currentLat = endpoint === 'origin' ? this.release?.originLatitude : this.release?.destinationLatitude;
    const currentLng = endpoint === 'origin' ? this.release?.originLongitude : this.release?.destinationLongitude;

    if (currentId) {
      this.selectedLocation = {
        id: currentId,
        name: currentName || '',
        location: {
          latitude: currentLat,
          longitude: currentLng
        }
      } as Location;
    } else {
      this.selectedLocation = null;
    }
    this.locationDialogVisible = true;
  }

  assignSelectedLocation(): void {
    const releaseId = this.release?.releaseId;
    const locationId = this.selectedLocation?.id;
    if (!releaseId || !locationId) {
      return;
    }
    this.isAssigningLocation = true;
    this.service.assignReleaseLocation(releaseId, {
      endpoint: this.locationDialogEndpoint,
      locationId
    }).subscribe({
      next: release => {
        this.release = release;
        this.locationDialogVisible = false;
        this.selectedLocation = null;
        this.isAssigningLocation = false;
        this.loadTracking(release);
        this.loadPlannedShipment(release);
        this.messageService.add({
          severity: 'success',
          summary: 'Location updated',
          detail: `${this.locationDialogEndpoint === 'origin' ? 'Pickup' : 'Dropoff'} location updated successfully.`
        });
      },
      error: error => {
        this.isAssigningLocation = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Location update failed',
          detail: error?.error?.message || 'Could not update location.'
        });
      }
    });
  }

  createLocationForEndpoint(): void {
    this.locationDialogVisible = false;
    this.router.navigate(this.locationCreateRoute(), {
      queryParams: this.locationCreateQuery(this.locationDialogEndpoint)
    });
  }

  locationDialogTitle(): string {
    if (this.locationDialogEndpoint === 'origin') {
      return this.release?.originLocationId ? 'Change Pickup Location' : 'Add Pickup Location';
    }
    return this.release?.destinationLocationId ? 'Change Dropoff Location' : 'Add Dropoff Location';
  }

  isCurrentEndpointAssigned(): boolean {
    return this.locationDialogEndpoint === 'origin'
      ? !!this.release?.originLocationId
      : !!this.release?.destinationLocationId;
  }

  currentEndpointLocationName(): string {
    return this.locationDialogEndpoint === 'origin'
      ? (this.release?.originName || '')
      : (this.release?.destinationName || '');
  }

  currentEndpointCoordinates(): string {
    const lat = this.locationDialogEndpoint === 'origin' ? this.release?.originLatitude : this.release?.destinationLatitude;
    const lng = this.locationDialogEndpoint === 'origin' ? this.release?.originLongitude : this.release?.destinationLongitude;
    if (lat != null && lng != null) {
      return `${lat}, ${lng}`;
    }
    return '';
  }

  assignButtonLabel(): string {
    return this.isCurrentEndpointAssigned() ? 'Update Location' : 'Assign Location';
  }

  canAddToShipment(): boolean {
    if (!this.release?.releaseId) return false;
    if (this.release.plannedShipmentId) return false;
    if (this.releaseRole() === 'PARENT') return false;
    const status = (this.release.releaseStatus || '').toUpperCase();
    if (status === 'CANCELLED' || status === 'CLOSED' || status === 'COMPLETED') return false;
    return true;
  }

  addToShipment(): void {
    if (!this.release?.releaseId) return;
    this.router.navigate(['/transport-planning/releases/add-to-shipment'], {
      queryParams: { releaseIds: this.release.releaseId }
    });
  }

  canBuildTruckPlan(): boolean {
    if (!this.release?.releaseId) return false;
    if (this.release.plannedShipmentId) return false;
    if (this.releaseRole() === 'PARENT') return false;
    const status = (this.release.releaseStatus || '').toUpperCase();
    if (status === 'CANCELLED' || status === 'CLOSED' || status === 'COMPLETED' || status === 'WAITING_PREVIOUS_LEG') return false;
    if (this.release.validationStatus !== 'READY') return false;
    return true;
  }

  buildTruckPlan(): void {
    if (!this.release?.releaseId) return;
    this.router.navigate(['/transport-planning/plans/create'], {
      queryParams: { releaseIds: this.release.releaseId }
    });
  }

  locationLabel(location?: Location | null): string {
    if (!location) {
      return '';
    }
    return [location.name, location.externalLocationReference].filter(Boolean).join(' / ') || `Location #${location.id}`;
  }

  locationMeta(location?: Location | null): string {
    if (!location) {
      return '';
    }
    const coordinates = location.location
      ? `${location.location.latitude}, ${location.location.longitude}`
      : 'Coordinates not set';
    return [location.locationType, coordinates].filter(Boolean).join(' | ');
  }

  lineTitle(line: TransportOrderReleaseLine): string {
    const item = this.inventoryItemForLine(line);
    return [
      line.itemNumber || item?.itemNumber || line.skuNumber || item?.skuNumber || line.itemCode || line.customerItemNumber,
      line.itemName || item?.itemName || line.itemDescription
    ]
      .filter(Boolean)
      .join(' - ') || 'Transport demand';
  }

  lineDescription(line: TransportOrderReleaseLine): string {
    return line.itemDescription || line.itemName || this.inventoryItemForLine(line)?.itemDescription || '-';
  }

  lineQuantity(line: TransportOrderReleaseLine): string {
    const quantity = Number(line.quantity ?? line.units ?? 0);
    return `${quantity > 0 ? this.formatAmount(quantity) : '-'} ${line.uom || 'unit'}`;
  }

  lineSource(line: TransportOrderReleaseLine): string {
    return line.sourceLineId || this.release?.sourceNumber || '-';
  }

  inventoryItemRoute(line: TransportOrderReleaseLine): unknown[] {
    return ['/warehousing/items/view', line.inventoryItemId || 0];
  }

  inventoryItemForLine(line: TransportOrderReleaseLine): WarehousingItem | undefined {
    return line.inventoryItemId ? this.inventoryItemsById.get(line.inventoryItemId) : undefined;
  }

  inventoryItemTitle(line: TransportOrderReleaseLine): string {
    const item = this.inventoryItemForLine(line);
    return item?.itemName || line.itemName || line.itemDescription || '-';
  }

  inventoryItemLinkLabel(line: TransportOrderReleaseLine): string {
    const item = this.inventoryItemForLine(line);
    return line.itemNumber
      || item?.itemNumber
      || line.skuNumber
      || item?.skuNumber
      || line.itemCode
      || line.customerItemNumber
      || item?.itemName
      || 'View warehouse item';
  }

  inventoryCodeDetails(line: TransportOrderReleaseLine): string {
    const item = this.inventoryItemForLine(line);
    return [
      line.itemNumber || line.itemCode || item?.itemNumber,
      line.skuNumber || item?.skuNumber ? `SKU ${line.skuNumber || item?.skuNumber}` : '',
      line.customerItemNumber || item?.customerItemNumber ? `Customer ${line.customerItemNumber || item?.customerItemNumber}` : '',
      line.itemBarcode || item?.itemBarcode ? `Barcode ${line.itemBarcode || item?.itemBarcode}` : ''
    ].filter(Boolean).join(' | ') || '-';
  }

  inventoryPhysicalDetails(line: TransportOrderReleaseLine): string {
    const item = this.inventoryItemForLine(line);
    const length = line.lengthMeters ?? item?.lengthMeters;
    const breadth = line.breadthMeters ?? item?.breadthMeters;
    const height = line.heightMeters ?? item?.heightMeters;
    const size = length && breadth && height ? `${length}x${breadth}x${height} ${item?.lengthUnitName || 'm'}` : '';
    return [
      line.tenantUomName || item?.tenantUomName,
      size,
      item?.weightKg != null ? `${this.formatAmount(item.weightKg)} kg/item` : '',
      item?.cbm != null ? `${this.formatAmount(item.cbm)} cbm/item` : ''
    ].filter(Boolean).join(' | ');
  }

  sourceWarehouseLabel(line: TransportOrderReleaseLine): string {
    if (!line.sourceWarehouseId) {
      return 'Not mapped';
    }
    const warehouse = this.warehousesById.get(line.sourceWarehouseId);
    return warehouse?.name || warehouse?.code || 'Warehouse record missing';
  }

  zoneConditionLabel(line: TransportOrderReleaseLine): string {
    const zone = line.sourceZoneId ? this.zonesById.get(line.sourceZoneId)?.name : '';
    const condition = line.conditionId ? this.conditionsById.get(line.conditionId)?.name : '';
    return [zone || 'No zone', condition || 'No condition'].join(' / ');
  }

  totalQuantity(): number {
    return this.getLines().reduce((sum, line) => sum + Number(line.quantity ?? line.units ?? 0), 0);
  }

  totalUnits(): number {
    return this.getLines().reduce((sum, line) => sum + Number(line.units ?? line.quantity ?? 0), 0);
  }

  totalWeight(): number {
    return this.getLines().reduce((sum, line) => sum + Number(line.weightKg ?? 0), 0);
  }

  totalVolume(): number {
    return this.getLines().reduce((sum, line) => sum + Number(line.volumeCbm ?? 0), 0);
  }

  totalPallets(): number {
    return this.getLines().reduce((sum, line) => sum + Number(line.pallets ?? 0), 0);
  }

  sharedUom(): string {
    const values = Array.from(new Set(this.getLines().map(line => line.uom).filter(Boolean)));
    return values.length === 1 ? values[0] || 'units' : 'units';
  }

  private sharedUomForLines(lines: TransportOrderReleaseLine[]): string {
    const values = Array.from(new Set(lines.map(line => line.uom).filter(Boolean)));
    return values.length === 1 ? values[0] || 'units' : 'units';
  }

  needDate(): string | Date | undefined {
    return this.release?.deliveryWindowEnd
      || this.release?.deliveryWindowStart
      || this.release?.pickupWindowEnd
      || this.release?.pickupWindowStart;
  }

  trackingNumber(): string {
    return this.release?.customerTrackingNumber || '-';
  }

  customerLabel(release: TransportOrderRelease | undefined = this.release): string {
    if (!release) return '-';
    return release.crmClientName
      || release.companyName
      || release.customerName
      || (release.crmClientId ? `CRM Client #${release.crmClientId}` : '')
      || (release.companyId ? `Company #${release.companyId}` : '')
      || (release.customerId ? `Customer #${release.customerId}` : '')
      || 'Customer missing';
  }

  accountLabel(release: TransportOrderRelease | undefined = this.release): string {
    if (!release) return '';
    return release.crmAccountName || (release.crmAccountId ? `Account #${release.crmAccountId}` : '');
  }

  customerHint(release: TransportOrderRelease | undefined = this.release): string {
    if (!release) return '';
    return this.accountLabel(release)
      || (release.crmClientContactId ? `CRM Contact ID ${release.crmClientContactId}` : '')
      || (release.crmClientId ? `CRM Client ID ${release.crmClientId}` : '')
      || (release.companyId || release.customerId ? 'Legacy customer context' : 'No CRM or legacy customer context');
  }

  hasCustomerContext(release: TransportOrderRelease | undefined = this.release): boolean {
    return !!(release?.crmClientId || release?.crmAccountId || release?.customerId || release?.companyId);
  }

  releaseRole(): string {
    return this.release?.releaseRole || (this.release?.parentReleaseId ? 'CHILD' : 'DIRECT');
  }

  roleLabel(): string {
    const role = this.releaseRole();
    if (role === 'PARENT') return 'Cross-dock parent';
    if (role === 'CHILD') {
      return this.release?.legSequence && this.release?.legCount
        ? `Leg ${this.release.legSequence} of ${this.release.legCount}`
        : 'Cross-dock leg';
    }
    return 'Direct release';
  }

  roleSeverity(): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    const role = this.releaseRole();
    if (role === 'PARENT') return 'warn';
    if (role === 'CHILD') return 'info';
    return 'secondary';
  }

  crossDockLegs(): PublicTrackingLeg[] {
    return this.tracking?.legs ?? [];
  }

  legTitle(leg: PublicTrackingLeg): string {
    return leg.legSequence && leg.legCount ? `Leg ${leg.legSequence} of ${leg.legCount}` : leg.releaseNumber || 'Leg';
  }

  legLoadSummary(leg: PublicTrackingLeg): string {
    const lines = leg.lines ?? [];
    const quantity = lines.reduce((sum, line) => sum + Number(line.quantity ?? line.units ?? 0), 0);
    return `${this.formatAmount(quantity)} ${this.sharedUomForLines(lines)} / ${lines.length} line${lines.length === 1 ? '' : 's'}`;
  }

  publicTrackingRoute(): unknown[] {
    return ['/track', this.release?.customerTrackingNumber || ''];
  }

  publicTrackingUrl(): string {
    const apiUrl = this.tracking?.trackingUrl;
    if (apiUrl?.startsWith('http')) {
      return apiUrl;
    }
    if (apiUrl?.startsWith('/')) {
      return `${window.location.origin}${apiUrl}`;
    }
    const path = this.router.serializeUrl(this.router.createUrlTree(this.publicTrackingRoute()));
    return `${window.location.origin}${path}`;
  }

  openPublicTracking(): void {
    if (!this.canViewPublicTracking()) {
      return;
    }
    window.open(this.publicTrackingUrl(), '_blank', 'noopener');
  }

  copyPublicTrackingLink(): void {
    if (!this.canViewPublicTracking()) {
      return;
    }
    this.copyToClipboard(this.publicTrackingUrl());
  }

  canSharePublicTrackingOnWhatsapp(): boolean {
    return this.canViewPublicTracking() && !!this.normalizedPhone(this.releaseCustomer?.phoneNumber);
  }

  sharePublicTrackingOnWhatsapp(): void {
    const phone = this.normalizedPhone(this.releaseCustomer?.phoneNumber);
    if (!phone || !this.canViewPublicTracking()) {
      return;
    }
    const text = [
      `Order release tracking${this.release?.releaseNumber ? ` for ${this.release.releaseNumber}` : ''}:`,
      this.publicTrackingUrl()
    ].join(' ');
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
  }

  canViewPublicTracking(): boolean {
    return !!this.release?.customerTrackingNumber;
  }

  canViewSource(): boolean {
    return this.release?.sourceType === 'SALES_ORDER' && Number.isFinite(Number(this.release?.sourceId));
  }

  viewSource(): void {
    if (!this.canViewSource()) {
      return;
    }
    this.router.navigate(['/order/sales-order/view', Number(this.release?.sourceId)]);
  }

  plannedShipmentRoute(): unknown[] {
    return ['/shipment/view', this.release?.plannedShipmentId || 0];
  }

  plannedShipmentHint(): string {
    return this.plannedShipment?.externalReference
      || this.plannedShipment?.primaryTransportReleaseNumber
      || this.release?.plannedShipmentId?.toString()
      || '-';
  }

  logisticsProjectRoute(): unknown[] {
    return ['/logistics-projects/logistics-projects', this.release?.logisticsProjectId || 0];
  }

  logisticsProjectLabel(): string {
    return this.release?.logisticsProjectNumber
      || this.logisticsProjectNumber
      || 'Logistics Project';
  }

  hasLogisticsProject(): boolean {
    return !!(this.release?.logisticsProjectId || this.release?.logisticsProjectNumber);
  }

  primaryShipmentLabel(): string {
    const shipment = this.tracking?.shipments?.find(item => item.shipmentId === this.release?.plannedShipmentId)
      || this.tracking?.shipments?.[0];
    return this.plannedShipment?.shipmentNumber
      || shipment?.shipmentNumber
      || (this.release?.plannedShipmentId ? `Shipment #${this.release.plannedShipmentId}` : 'Shipment');
  }

  plannedShipmentStatus(): string {
    return this.plannedShipment?.shipmentStatus || this.plannedShipment?.workflowState || (this.release?.plannedShipmentId ? 'Created' : 'Not created');
  }

  plannedDeliveries(): ShipmentDelivery[] {
    return this.plannedShipment?.deliveries || [];
  }

  primaryDelivery(): ShipmentDelivery | undefined {
    const releaseId = this.release?.releaseId;
    return this.plannedDeliveries().find(delivery => delivery.primaryTransportReleaseId === releaseId)
      || this.plannedDeliveries().find(delivery => delivery.crossDockOrchestrationId === this.release?.crossDockOrchestrationId)
      || this.plannedDeliveries()[0];
  }

  primaryDeliveryLabel(): string {
    const delivery = this.primaryDelivery();
    if (delivery?.deliveryNumber) {
      return delivery.deliveryNumber;
    }
    const trackingDelivery = this.tracking?.deliveries?.[0];
    return trackingDelivery?.deliveryNumber
      || (delivery?.deliveryId ? `Delivery #${delivery.deliveryId}` : 'Not created');
  }

  primaryDeliveryRoute(): unknown[] {
    return ['/shipment/delivery/view', this.primaryDelivery()?.deliveryId || 0];
  }

  hasPrimaryDelivery(): boolean {
    return !!this.primaryDelivery()?.deliveryId;
  }

  primaryDeliveryStatus(): string {
    const delivery = this.primaryDelivery();
    return delivery?.jobStatus?.toString()
      || delivery?.workflowState
      || this.tracking?.deliveries?.[0]?.jobStatus
      || this.tracking?.deliveries?.[0]?.workflowState
      || '-';
  }

  planningDriverName(): string {
    const driver = this.primaryDelivery()?.deliveryDriver;
    return driver?.legalName || driver?.name || (this.release?.assignedDriverId ? `Driver #${this.release.assignedDriverId}` : '-');
  }

  planningVehicleLabel(): string {
    const vehicle = this.primaryDelivery()?.deliveryVehicle;
    return vehicle?.plateNumber
      || vehicle?.registrationNumber
      || (this.release?.assignedVehicleId ? `Vehicle #${this.release.assignedVehicleId}` : '-');
  }

  planningVehicleType(): string {
    const vehicle = this.primaryDelivery()?.deliveryVehicle;
    return vehicle?.type?.name || this.release?.vehicleTypeName || (this.release?.vehicleTypeId ? `Vehicle type #${this.release.vehicleTypeId}` : '-');
  }

  planningShipmentReference(): string {
    return this.plannedShipment?.externalReference
      || this.release?.plannedShipmentId?.toString()
      || '-';
  }

  planningMobilizationLabel(): string {
    return this.release?.assignedMobilizationId ? `Mobilization #${this.release.assignedMobilizationId}` : '-';
  }

  planningAssignmentModeLabel(): string {
    return this.release?.assignmentMode?.replaceAll('_', ' ') || '-';
  }

  orchestrationRoute(): unknown[] {
    return ['/transport-planning/orchestrations', this.release?.crossDockOrchestrationId || 0];
  }

  orchestrationTitle(): string {
    return this.release?.crossDockOrchestrationNumber
      || this.orchestration?.crossDockOrchestrationNumber
      || (this.release?.crossDockOrchestrationId ? `Orchestration #${this.release.crossDockOrchestrationId}` : '-');
  }

  orchestrationLegs() {
    return this.orchestration?.legs ?? [];
  }

  trackingEvents(): PublicTrackingEvent[] {
    return this.tracking?.events ?? [];
  }

  trackingDocuments(): PublicTrackingDocument[] {
    return this.tracking?.documents ?? [];
  }

  deliveryCount(): number {
    return this.tracking?.deliveries?.length ?? 0;
  }

  shipmentCount(): number {
    return this.tracking?.shipments?.length ?? (this.release?.plannedShipmentId ? 1 : 0);
  }

  eventIcon(event: PublicTrackingEvent): string {
    const type = (event.eventType || '').toUpperCase();
    if (type === 'SHIPMENT') return 'pi pi-truck';
    if (type === 'DELIVERY') return 'pi pi-send';
    if (type === 'SITE') return 'pi pi-map-marker';
    if (type === 'EVIDENCE') return 'pi pi-paperclip';
    if (type === 'WORKFLOW') return 'pi pi-sync';
    return 'pi pi-check';
  }

  documentSeverity(type?: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    const value = (type || '').toUpperCase();
    if (value === 'POD') return 'success';
    if (value === 'WAYBILL') return 'info';
    if (value === 'EVIDENCE') return 'warn';
    return 'secondary';
  }

  openDocument(document: PublicTrackingDocument): void {
    if (!document.url) {
      return;
    }
    window.open(document.url, '_blank', 'noopener');
  }

  formatDateTime(value?: string | Date | null): string {
    if (!value) {
      return 'Not set';
    }
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? 'Not set' : formatDate(date, 'dd MMM yyyy HH:mm', 'en-US');
  }

  formatAmount(value: number): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
  }

  private loadTracking(release: TransportOrderRelease): void {
    if (!release.customerTrackingNumber) {
      return;
    }
    this.service.getPublicOrderReleaseTracking(release.customerTrackingNumber).subscribe({
      next: tracking => this.tracking = tracking,
      error: () => this.tracking = undefined
    });
  }

  private loadOrchestration(release: TransportOrderRelease): void {
    if (!release.releaseId || !release.crossDockOrchestrationId) {
      this.orchestration = undefined;
      return;
    }
    this.service.getReleaseCrossDockOrchestration(release.releaseId).subscribe({
      next: orchestration => this.orchestration = orchestration,
      error: () => this.orchestration = undefined
    });
  }

  private loadReleaseCustomer(release: TransportOrderRelease): void {
    if (!release.customerId) {
      return;
    }
    this.customerService.getCustomerById(release.customerId).subscribe({
      next: response => this.releaseCustomer = response.data,
      error: () => this.releaseCustomer = undefined
    });
  }

  private loadPlannedShipment(release: TransportOrderRelease): void {
    if (!release.plannedShipmentId) {
      this.plannedShipment = undefined;
      return;
    }
    this.shipmentService.getShipmentById(release.plannedShipmentId).subscribe({
      next: shipment => this.plannedShipment = shipment,
      error: () => this.plannedShipment = undefined
    });
  }

  private loadLogisticsProject(release: TransportOrderRelease): void {
    if (!release.logisticsProjectId || release.logisticsProjectNumber || !release.owningCompanyId) {
      this.logisticsProjectNumber = release.logisticsProjectNumber;
      return;
    }
    this.logisticsProjectsService.getProject(release.logisticsProjectId, release.owningCompanyId).subscribe({
      next: project => this.logisticsProjectNumber = project.projectNumber || project.projectName || undefined,
      error: () => this.logisticsProjectNumber = undefined
    });
  }

  private loadInventoryItems(release: TransportOrderRelease): void {
    const itemIds = Array.from(new Set((release.lines || [])
      .map(line => line.inventoryItemId)
      .filter((id): id is number => Number.isFinite(Number(id)) && Number(id) > 0)
      .map(id => Number(id))));
    if (!itemIds.length) {
      return;
    }
    forkJoin(itemIds.map(itemId => this.warehousingService.getItems({ itemId }))).subscribe({
      next: responses => {
        responses
          .flatMap(response => response.data || [])
          .filter(item => item.id)
          .forEach(item => this.inventoryItemsById.set(item.id!, item));
      },
      error: () => this.inventoryItemsById.clear()
    });
  }

  private loadLineMasterData(release: TransportOrderRelease): void {
    const lines = release.lines || [];
    const needsWarehouseLookup = lines.some(line => !!line.sourceWarehouseId);
    const needsZoneLookup = lines.some(line => !!line.sourceZoneId);
    const needsConditionLookup = lines.some(line => !!line.conditionId);
    if (!needsWarehouseLookup && !needsZoneLookup && !needsConditionLookup) {
      return;
    }
    forkJoin({
      warehouses: needsWarehouseLookup ? this.warehousingService.getMasterData('WAREHOUSE') : of(null),
      zones: needsZoneLookup ? this.warehousingService.getMasterData('ZONE') : of(null),
      conditions: needsConditionLookup ? this.warehousingService.getMasterData('ITEM_CONDITION') : of(null)
    }).subscribe({
      next: ({ warehouses, zones, conditions }) => {
        (warehouses?.data || []).forEach(item => item.id && this.warehousesById.set(item.id, item));
        (zones?.data || []).forEach(item => item.id && this.zonesById.set(item.id, item));
        (conditions?.data || []).forEach(item => item.id && this.conditionsById.set(item.id, item));
      },
      error: () => {
        this.warehousesById.clear();
        this.zonesById.clear();
        this.conditionsById.clear();
      }
    });
  }

  private normalizedPhone(phone?: string | null): string {
    return (phone || '').replace(/[^0-9]/g, '');
  }

  private copyToClipboard(value: string): void {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(value).catch(() => this.fallbackCopyToClipboard(value));
      return;
    }
    this.fallbackCopyToClipboard(value);
  }

  private fallbackCopyToClipboard(value: string): void {
    const input = document.createElement('textarea');
    input.value = value;
    input.setAttribute('readonly', 'true');
    input.style.position = 'fixed';
    input.style.opacity = '0';
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
  }

  private resolveTabIndex(tab: string | null): number {
    const index = this.tabKeys.findIndex(value => value === (tab || '').toLowerCase());
    return index >= 0 ? index : 0;
  }
}
